/**
 * BILLING — turning the price catalogue into the two things a customer is
 * actually charged.
 *
 * `GET /business-prices` returns one flat object of ~40 priced items, each
 * shaped `{label, stepId, service, price: {domestic, international, ourCost,
 * monthly}}`. Two completely different billing models are folded into that one
 * payload and the old page never separated them:
 *
 *   SUBSCRIPTION — `price.monthly`, charged every month whether or not it is
 *   used. Exactly three items carry one: Onboarding ($19.99), Simplici Sign
 *   ($4.99) and Simplici Text ($19.99).
 *
 *   PER USE — `price.domestic` / `price.international`, charged per event.
 *   26 items carry one, from Simplici Text at $0.05 to a Wire at $25 domestic
 *   and $35 international.
 *
 * They are not alternatives and one cannot be derived from the other, so this
 * file splits them and never mixes them. `ourCost` is Simplici's own cost, not
 * the customer's, and is deliberately never surfaced.
 */

export interface IPriceBlock {
	domestic?: number;
	international?: number;
	ourCost?: number;
	monthly?: number;
}

export interface ICatalogItem {
	label?: string;
	stepId?: string;
	service?: string;
	price?: IPriceBlock;
}

export type Catalog = Record<string, ICatalogItem>;

/** How a rate reads once it has been classified. */
export type RateKind = 'priced' | 'included' | 'unavailable';

export interface IRate {
	key: string;
	label: string;
	service: string;
	kind: RateKind;
	/** The Actions List step this belongs to. */
	step?: string | null;
	/** The step's own display name, straight from the catalogue. */
	stepLabel?: string;
	/** The step's position in the catalogue — the catalogue defines the order. */
	order?: number;
	/** The catalogue's description of what the action does. */
	note?: string;
	domestic: number | null;
	international: number | null;
	/** Set only on a banded rate — see `BAND_RE`. */
	band?: { of: string; from: number; to: number | null };
}

export interface ISubscriptionService {
	key: string;
	label: string;
	monthly: number;
	/** From `GET /subscriptions` — whether this account actually holds it. */
	active: boolean;
}

/**
 * Volume bands arrive as separate sibling keys —
 * `PRTHAccountCountRange1` (1-500), `…Range2` (501-1000), `…Range3` (1000+) —
 * which would otherwise draw as three near-identical rows saying almost the
 * same thing. They are detected here and collapsed into one row carrying its
 * tiers, so the table says "PRTH Account Creation" once.
 */
const BAND_RE = /^(.*?)Range(\d+)$/;

/** `PRTH Account Creation (1-500)` → the range, so tiers can be sorted. */
const rangeOf = (label: string): { from: number; to: number | null } | null => {
	const m = label.match(/\((\d[\d,]*)\s*[-–]\s*(\d[\d,]*)\)/);
	if (m?.[1] && m?.[2])
		return { from: +m[1].replace(/,/g, ''), to: +m[2].replace(/,/g, '') };
	const open = label.match(/\((\d[\d,]*)\+\)/);
	if (open?.[1]) return { from: +open[1].replace(/,/g, ''), to: null };
	return null;
};

/** Several catalogue entries use the raw key as their label (`aml`, `ssn`). */
const titleise = (key: string) =>
	key
		.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
		.replace(/^./, c => c.toUpperCase());

const num = (v: unknown): number | null =>
	typeof v === 'number' && Number.isFinite(v) ? v : null;

/**
 * THE SUBSCRIPTION PICTURE — assembled from two payloads that DO NOT SHARE A
 * KEY SPACE, which is the trap here.
 *
 *   `GET /business-prices` is keyed by SERVICE — `onboardingService`,
 *   `simpliciSignService`, `simpliciTextService` — each with a `price.monthly`.
 *   That is the catalogue: what each service costs.
 *
 *   `GET /subscriptions` is keyed by ACCOUNT TYPE — `{entity: {status, price}}`
 *   or `individual`. That is what this account actually holds.
 *
 * There is no join between them. An earlier version of this file matched
 * `onboardingService` -> `onboarding` against the subscription keys and found
 * nothing, so a subscribed account rendered as "No active subscription" while
 * the API plainly said `entity: {status: true, price: 19.99}`.
 *
 * So the two are reported separately and honestly: `held` is what the account
 * is on and being charged, `catalogue` is what each service lists at. They are
 * deliberately NOT merged — merging them is precisely the conflation that makes
 * `simplici.api/src/services/pricing` relabel service prices as account-type
 * prices, which is the defect flagged in spec 18.
 */
export const heldSubscriptions = (
	subscriptions: Record<string, {status?: boolean; price?: number}> | null
): ISubscriptionService[] => {
	if (!subscriptions) return [];
	return Object.entries(subscriptions)
		.filter(([, v]) => v && typeof v === 'object')
		.map(([key, v]) => ({
			key,
			/* `entity` -> `Entity / Company`, the words the product uses. */
			label:
				key === 'entity'
					? 'Entity / Company'
					: key === 'individual'
					? 'Individual'
					: key === 'evault'
					? 'eVault add-on'
					: titleise(key),
			monthly: num(v?.price) ?? 0,
			active: v?.status === true,
		}))
		.sort((a, b) => b.monthly - a.monthly);
};

/**
 * The plan head's call to action, or null when there is nothing to launch.
 *
 * SIM-52. The launcher used to hide behind "no active plan" — and an individual
 * on the individual plan IS an active plan, so the one account that can be
 * upgraded was the one with no way to start. Only a held ENTITY plan ends the
 * road. An individual account on the individual plan is offered the upgrade;
 * an entity that holds only the individual plan (upgraded, not yet subscribed)
 * is offered Subscribe, as the legacy screen labelled it. With the account type
 * unknown (`/users` failed) either launcher could be the wrong one, so neither
 * is offered until it is known. No plan at all is always Subscribe — that is
 * how a fresh business starts the journey.
 */
export const planCta = (
	activePlanKey: string | undefined,
	accountType?: string | null
): string | null => {
	if (activePlanKey === 'entity') return null;
	if (activePlanKey !== 'individual') return 'Subscribe';
	if (accountType === 'entity') return 'Subscribe';
	if (accountType === 'individual') return 'Upgrade to Entity/Company';
	return null;
};

/** Every subscription service the catalogue prices, held or not. */
export const catalogueServices = (
	catalog: Catalog | null
): ISubscriptionService[] => {
	if (!catalog) return [];
	return Object.entries(catalog)
		.filter(([, v]) => !!num(v?.price?.monthly))
		.map(([key, v]) => ({
			key,
			label: v.label || titleise(key.replace(/Service$/, '')),
			monthly: num(v?.price?.monthly) as number,
			/* The catalogue cannot say what is held — only `/subscriptions` can,
			   and it answers in a different vocabulary. Never guess `true`. */
			active: false,
		}))
		.sort((a, b) => b.monthly - a.monthly);
};

/** What the account pays every month, before any usage. */
export const monthlyTotal = (services: ISubscriptionService[]) =>
	services.filter(s => s.active).reduce((sum, s) => sum + s.monthly, 0);


/**
 * THE RATE CARD COMES FROM THE FLOW BUILDER'S OWN CATALOGUE.
 *
 * It was built from `GET /business-prices`, which is the wrong book. That is the
 * historical price list — ~40 internal line items, many for capabilities the
 * flow builder no longer offers, and its numbers DISAGREE with what the builder
 * shows: `kycComprehensive` $12 there against `allId` $0.75 in the catalogue,
 * `kyb` $5 against `kybVerification` $5, `aml` $0.35 against $0.45 in the raw
 * defaults. A customer configuring a flow sees one number and would have been
 * quoted another on the billing page.
 *
 * `GET /pipeline-init` is the authority. It is the same endpoint the Actions
 * List renders from, and it resolves the catalogue AGAINST THIS BUSINESS —
 * measured: the raw `master-pipelines` defaults are kyc $1 / aml $0.45 /
 * accreditation $5 / kyb $2, while `/pipeline-init` answers $0.75 / $0.35 / $4
 * / $5 for this account. The resolved figure is the one the customer is
 * charged, so it is the one the rate card must print.
 *
 * Removing what is no longer in the catalogue then costs nothing: an action the
 * builder does not serve simply is not in the response.
 */
export interface ICatalogStep {
	key?: string;
	label?: string;
	actions?: Array<{
		key?: string;
		label?: string;
		description?: string;
		price?: number;
		/** Set when one action is priced differently per selected option —
		 *  accreditation 506(b) vs 506(c). Resolved server-side. */
		variantPrices?: Array<{key: string; label: string; price: number}>;
		/** `from` means the action's own `price` is a FLOOR, not a rate — the
		 *  per-country data-validation card. See priceRange on /pipeline-init. */
		priceKind?: string;
		/** The individually purchasable checks inside one action. The KYC step
		 *  sells four of them this way. */
		metadata?: Array<{
			key?: string;
			label?: string;
			description?: string;
			price?: number;
		}>;
	}>;
}

/*
 * NO HARDCODED STEP ORDER.
 *
 * This was a list of step keys in this file, which meant a catalogue change —
 * a new step, a reordered flow — silently sorted wrong on the billing page
 * while the flow builder showed the truth. `/pipeline-init` returns the steps
 * in the order the Actions List renders them, so that order IS the answer;
 * rows carry their step's index and sort by it.
 */
export const catalogRates = (steps: ICatalogStep[] | null): IRate[] => {
	if (!steps?.length) return [];
	const rows: IRate[] = [];

	steps.forEach((step, stepIndex) => {
		(step.actions ?? []).forEach(a => {
			/* ONE ACTION, MANY PURCHASABLE CHECKS.
			   The KYC step is a single `allId` action whose METADATA carries the
			   checks a customer actually buys and is billed for — document
			   validation $0.75, data validation, the $5 criminal background
			   check. Reading only `action.price` left every one of them off the
			   rate card: the page a customer checks a bill against had no line
			   for a charge that appears on the bill. Emitted like variantPrices,
			   one row per priced check. */
			const priced = (a.metadata ?? []).filter(
				m => typeof m?.price === 'number'
			);
			if (priced.length) {
				priced.forEach(m => {
					const p = Number(m.price);
					rows.push({
						order: stepIndex,
						key: `${step.key}:${a.key}:${m.key}`,
						label: m.label || m.key || '—',
						service: step.key || '',
						step: step.key || null,
						stepLabel: step.label || step.key || '',
						note: m.description || a.description || '',
						kind: p > 0 ? 'priced' : 'included',
						domestic: Number.isFinite(p) ? p : null,
						international: null,
					});
				});
				/* The action's own figure is then either a duplicate of these or,
				   when it is a `from` floor, a number nobody is ever charged —
				   $0.06 for "KYC & AML" reads as a rate and is not one. */
				return;
			}

			/* No price on the action means the catalogue does not bill it —
			   distinct from a price of zero, which means "included". */
			if (a?.price === undefined || a?.price === null) return;

			/* ONE ACTION, TWO PRICES, TWO ROWS.
			   Accreditation is a single builder action with a 506b/506c selector
			   priced $4 and $10. Printing one row at the cheaper figure would
			   quote a customer a price they may not pay, so each variant gets its
			   own line — the rate card's job is to be the thing you can check a
			   bill against. */
			if (a.variantPrices?.length) {
				a.variantPrices.forEach(v => {
					rows.push({
						order: stepIndex,
						key: `${step.key}:${a.key}:${v.key}`,
						label: v.label,
						service: step.key || '',
						step: step.key || null,
						stepLabel: step.label || step.key || '',
						note: a.description || '',
						kind: v.price > 0 ? 'priced' : 'included',
						domestic: v.price,
						international: null,
					});
				});
				return;
			}

			const price = Number(a.price);
			rows.push({
				order: stepIndex,
				key: `${step.key}:${a.key}`,
				label: a.label || a.key || '—',
				service: step.key || '',
				step: step.key || null,
				stepLabel: step.label || step.key || '',
				note: a.description || '',
				kind: price > 0 ? 'priced' : 'included',
				domestic: Number.isFinite(price) ? price : null,
				/* The catalogue prices one figure per action; there is no
				   domestic/international split here, so the column is absent
				   rather than duplicated. */
				international: null,
			});
		});
	});

	/* Catalogue order first, then dearest first inside a step. */
	return rows.sort(
		(a, b) =>
			(a.order ?? 0) - (b.order ?? 0) ||
			(b.domestic ?? 0) - (a.domestic ?? 0) ||
			a.label.localeCompare(b.label)
	);
};

/**
 * EVERY PER-USE RATE, WITH ZERO TREATED AS A REAL ANSWER.
 *
 * A $0 item (geolocation, device biometric, OCR, web component) is INCLUDED at
 * no charge — which is information a customer wants — and is not the same as a
 * rate the catalogue does not carry. Drawing them alike, or hiding the zeroes,
 * loses that. `unavailable` is reserved for an item with no usable number at
 * all.
 */
export const perUseRates = (catalog: Catalog | null): IRate[] => {
	if (!catalog) return [];

	const rows: IRate[] = [];
	const bands = new Map<string, IRate>();

	Object.entries(catalog).forEach(([key, v]) => {
		if (!v || typeof v !== 'object' || !v.price) return;
		/* A subscription-only item is not a per-use rate. */
		if (num(v.price.monthly) && !num(v.price.domestic) && !num(v.price.international))
			return;

		const dom = num(v.price.domestic);
		const intl = num(v.price.international);
		const label = v.label && v.label !== key ? v.label : titleise(key);
		const kind: RateKind =
			dom === null && intl === null
				? 'unavailable'
				: (dom ?? 0) === 0 && (intl ?? 0) === 0
				? 'included'
				: 'priced';

		const m = key.match(BAND_RE);
		if (m?.[1]) {
			const stem = m[1];
			const range = rangeOf(label);
			const head = bands.get(stem);
			const tier: IRate = {
				key,
				/* `PRTH Account Creation (1-500)` -> `PRTH Account Creation` */
				label: label.replace(/\s*\([^)]*\)\s*$/, ''),
				service: v.service || stem,
					kind,
				domestic: dom,
				international: intl,
				band: range
					? { of: stem, from: range.from, to: range.to }
					: { of: stem, from: 0, to: null },
			};
			if (!head) {
				bands.set(stem, tier);
				rows.push(tier);
			}
			return;
		}

		rows.push({
			key,
			label,
			service: v.service || key,
			kind,
			domestic: dom,
			international: intl,
		});
	});

	const rank = (r: IRate) =>
		r.kind === 'priced' ? 0 : r.kind === 'included' ? 1 : 2;
	return rows.sort(
		(a, b) =>
			rank(a) - rank(b) ||
			(b.domestic ?? 0) - (a.domestic ?? 0) ||
			a.label.localeCompare(b.label)
	);
};

/** All tiers of one banded rate, for the expanded row. */
export const bandTiers = (catalog: Catalog | null, stem: string): IRate[] => {
	if (!catalog) return [];
	return Object.entries(catalog)
		.filter(([k]) => k.startsWith(stem) && BAND_RE.test(k))
		.map(([key, v]) => {
			const label = v.label || titleise(key);
			const range = rangeOf(label);
			return {
				key,
				label,
				service: v.service || stem,
				kind: 'priced' as RateKind,
				domestic: num(v.price?.domestic),
				international: num(v.price?.international),
				band: range ? { of: stem, from: range.from, to: range.to } : undefined,
			};
		})
		.sort((a, b) => (a.band?.from ?? 0) - (b.band?.from ?? 0));
};

/**
 * INTERNATIONAL IS ONLY A COLUMN WHEN IT SAYS SOMETHING.
 *
 * Most items carry `international: 0` where the real meaning is "same as
 * domestic" or "not offered", and a column of zeroes reads as "free abroad",
 * which is wrong in both cases. The column is shown only when at least one rate
 * genuinely differs across the two.
 */
export const hasInternationalSpread = (rates: IRate[]) =>
	rates.some(
		r =>
			r.kind === 'priced' &&
			r.international !== null &&
			r.international !== r.domestic
	);

/**
 * A UNIT RATE, WITHOUT POINTLESS ZEROS.
 *
 * `money()` always renders two decimals, which is right for a total in a
 * column of totals — the decimal points line up and the eye can add. It is
 * wrong for a rate card, where `$4.00` and `$10.00` are just `$4` and `$10`
 * carrying noise, and the zeros compete with the figures that DO have cents
 * ($0.75, $0.35). Whole amounts lose the decimals; fractional ones keep both.
 */
export const unitMoney = (n: number | null | undefined) => {
	if (n === null || n === undefined) return '—';
	return Number.isInteger(n)
		? n.toLocaleString()
		: n.toLocaleString(undefined, {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
		  });
};

export const money = (n: number | null | undefined) =>
	n === null || n === undefined
		? '—'
		: n.toLocaleString(undefined, {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
		  });

/** `1-500`, `1,000+`. */
export const bandLabel = (b: IRate['band']) =>
	!b ? '' : b.to === null ? `${b.from.toLocaleString()}+` : `${b.from.toLocaleString()}–${b.to.toLocaleString()}`;

/**
 * The hosted asset for a card brand.
 *
 * Same filenames the old payment list uses (`component/paymentMethodList/
 * card-list.tsx`), so both surfaces show the same artwork and there is one
 * place to add a brand. Matched case-insensitively because Stripe returns
 * `visa` while `/payment-methods` answers `Visa`, and a case mismatch would
 * silently fall through to the generic card for every row.
 */
export const cardBrandImage = (brand?: string): string => {
	switch (String(brand ?? '').trim().toLowerCase()) {
		case 'visa':
			return 'visa-card.svg';
		case 'mastercard':
		case 'master card':
			return 'master-card.svg';
		case 'discover':
			return 'discover-card.svg';
		case 'maestrocard':
		case 'maestro':
			return 'maestro-card.svg';
		case 'american express':
		case 'amex':
			return 'american-express-card.svg';
		default:
			return 'default-card.svg';
	}
};

/* ═══════════════════════════════════════════════════════════════════════════
   PAYMENT METHODS — the rules derived once (spec 18 §18.2)
   ═══════════════════════════════════════════════════════════════════════════ */

export interface ICardMethod {
	id?: string;
	brand?: string;
	last4?: string;
	exp_month?: number;
	exp_year?: number;
	default?: boolean;
}
export interface IBankMethod {
	_id?: string;
	bankName?: string;
	/* `accountId` is the field the set-default payload wants — distinct from the
	   account's own `id`, and the reason a bank row needs both to be chipped. */
	accounts?: Array<{
		id?: string;
		accountId?: string;
		name?: string;
		mask?: string;
		subtype?: string;
	}>;
}
export interface IWireMethod {
	bankName?: string;
	accountNumber?: string;
	routingNumber?: string;
}

/** A card is expired once its expiry month has fully passed. */
export const isCardExpired = (c?: ICardMethod | null): boolean => {
	if (!c?.exp_year || !c?.exp_month) return false;
	return new Date(c.exp_year, c.exp_month, 1).getTime() <= Date.now();
};

/**
 * IS THERE ANYTHING TO CHARGE?
 *
 * Was `cards.length || wire?.accountNumber`, which is wrong twice. An ACH-only
 * account has no cards and got a false "no payment method on file" alert. And
 * wire INSTRUCTIONS are always present once provisioned — counting them as a
 * method meant the alert could never fire for anyone who had them, which is the
 * more dangerous half: the warning silently stopped working.
 *
 * Wire counts only when it is the designated default, because that is the only
 * state in which it is what will actually be charged.
 */
export const hasAnyMethod = (
	cards: ICardMethod[],
	banks: IBankMethod[],
	def?: {method?: string | null}
): boolean =>
	cards.length > 0 ||
	banks.some(b => (b.accounts?.length ?? 0) > 0) ||
	def?.method === 'wire';

export type MethodKind = 'card' | 'ach' | 'wire' | null;

/**
 * WHAT WILL ACTUALLY BE CHARGED.
 *
 * The payload carries TWO default signals that can disagree —
 * `default.method` and a card's own `default: true` — and the deck read only
 * the second, so an account defaulting to ACH or wire still displayed a card
 * as its payment method.
 *
 * Order: an explicit designation wins; then a card flagged default; then, if
 * exactly one method exists at all, that one — but reported as `designated:
 * false`, because nothing designated it and a "default" badge should state a
 * fact rather than a guess.
 */
export const resolveDefault = (
	cards: ICardMethod[],
	banks: IBankMethod[],
	wire?: IWireMethod | null,
	def?: {method?: string | null; detail?: unknown}
): {
	kind: MethodKind;
	card?: ICardMethod | null;
	/** Which bank/account the designation names — needed to chip the right row. */
	bankId?: string | null;
	accountId?: string | null;
	designated: boolean;
} => {
	if (def?.method && def?.detail) {
		if (def.method === 'card') {
			/*
			 * `detail` IS NOT ALWAYS AN ID.
			 *
			 * The backend sets it to the whole card object
			 * (`cards.find(c => c.default === true)`), so comparing it to `c.id`
			 * never matched and every lookup silently fell through to `cards[0]`.
			 * On an account with two cards that means the UI can show the wrong
			 * one as default — and it looks right, because there IS a default
			 * chip, just on the wrong row.
			 */
			const detailId =
				typeof def.detail === 'string'
					? def.detail
					: (def.detail as {id?: string} | null)?.id;
			const byId = cards.find(c => c.id === detailId);
			return {
				kind: 'card',
				card: byId ?? cards[0] ?? null,
				bankId: null,
				accountId: null,
				designated: true,
			};
		}
		/* For ACH the designation names a bank AND an account within it; without
		   both, a business with two accounts chips every bank row. */
		const d = def.detail as {_id?: string; id?: string; accounts?: Array<{accountId?: string}>} | null;
		return {
			kind: def.method as MethodKind,
			card: null,
			bankId: d?._id ?? d?.id ?? null,
			accountId: d?.accounts?.[0]?.accountId ?? null,
			designated: true,
		};
	}

	const flagged = cards.find(c => c.default);
	if (flagged)
		return {kind: 'card', card: flagged, bankId: null, accountId: null, designated: true};

	const bankCount = banks.reduce((n, b) => n + (b.accounts?.length ?? 0), 0);
	const only =
		cards.length + bankCount + (wire?.accountNumber ? 1 : 0) === 1;
	if (only && cards.length === 1)
		return {kind: 'card', card: cards[0], bankId: null, accountId: null, designated: false};
	if (only && bankCount === 1)
		return {
			kind: 'ach',
			card: null,
			bankId: banks[0]?._id ?? null,
			accountId: banks[0]?.accounts?.[0]?.accountId ?? null,
			designated: false,
		};
	if (only && wire?.accountNumber)
		return {kind: 'wire', card: null, bankId: null, accountId: null, designated: false};

	return {kind: null, card: null, bankId: null, accountId: null, designated: false};
};

/**
 * The billing address as one readable line.
 *
 * `business.billingAddress` is always PRESENT and often entirely blank —
 * `{address1:"", address2:"", city:"", state:"", zip:"", country:""}` is what a
 * business that never filled it in looks like. Truthiness on the object is
 * therefore useless; the parts have to be inspected. Returns null when there is
 * genuinely nothing, so the caller can say "none saved" rather than render an
 * empty row or a line of stray commas.
 */
export const formatBillingAddress = (a?: {
	address1?: string;
	address2?: string;
	city?: string;
	state?: string;
	zip?: string;
	country?: string;
} | null): string | null => {
	if (!a) return null;
	const street = [a.address1, a.address2].map(v => (v ?? '').trim()).filter(Boolean);
	/* City/state/zip read as one unit — "Austin, TX 78701" — so they are joined
	   before the parts are, or a missing state leaves a dangling comma. */
	const locality = [
		[a.city, a.state].map(v => (v ?? '').trim()).filter(Boolean).join(', '),
		(a.zip ?? '').trim(),
	]
		.filter(Boolean)
		.join(' ');
	const parts = [...street, locality, (a.country ?? '').trim()].filter(Boolean);
	return parts.length ? parts.join(' · ') : null;
};

/**
 * WHAT THIS CYCLE'S BILL IS MADE OF — one computation, used by the tile AND the
 * metered card (spec 18 §7b: "the footer and the tile must never disagree").
 *
 * `/onboarding-billings` lists the account's RECURRING line items in the same
 * `services` groups as its per-use ones: after subscribing, `onboarding[]`
 * carries `{key: 'onboardingService', quantity: 1, price: 19.99}` next to the
 * KYB/AML rows, and `totalAmount` sums all of them. The three recurring keys
 * are the ones ending in `Service` — `onboardingService`, `simpliciSignService`,
 * `simpliciTextService` (spec 18 §1: "everything else is metered usage").
 *
 * The deck used to treat every row with a quantity as metered, and then build
 * month-to-date as `totalAmount + monthly`. So a fresh $19.99 subscription
 * showed as "1 metered item" worth $19.99 AND as the $19.99 plan, and the tile
 * read $39.98 — double the only charge the customer had made. Reported as
 * "twice the subscription charges".
 *
 * `price` on these rows is the LINE total, not a unit rate — see the note on
 * `usage` in use-billing-deck.ts; the unit is derived for display only.
 */
type ServiceLine = { name?: string; key?: string; price?: number; quantity?: number };
export interface IMeteredLine extends ServiceLine {
	total: number;
	unit: number | null;
}

/** A recurring line item, not usage. */
export const isSubscriptionLine = (key?: string): boolean =>
	typeof key === 'string' && /Service$/.test(key);

export const meteredLines = (
	groups: Record<string, ServiceLine[]> | null | undefined
): IMeteredLine[] => {
	const lines: IMeteredLine[] = [];
	Object.values(groups ?? {}).forEach(group => {
		if (!Array.isArray(group)) return;
		group.forEach(l => {
			const qty = Number(l?.quantity ?? 0);
			if (!qty) return; // nothing consumed — not a line on this bill
			if (isSubscriptionLine(l?.key)) return; // the plan: shown as the plan, once
			const total = Number(l?.price ?? 0);
			lines.push({ ...l, total, unit: total / qty });
		});
	});
	return lines.sort((a, b) => b.total - a.total);
};

/** plan (+ add-ons) + everything metered this cycle (usage + eVault transfers). */
export const monthToDateTotal = (monthly: number, metered: number): number =>
	Number(monthly || 0) + Number(metered || 0);
